import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { toast } from '../components/Toast';

export default function WarehouseList() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [stocksLoading, setStocksLoading] = useState(false);

  // Detail View & Transactions states
  const [viewingWarehouse, setViewingWarehouse] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('stock');

  // Search & Filter states
  const [stockSearch, setStockSearch] = useState('');
  const [txnSearch, setTxnSearch] = useState('');
  const [txnTypeFilter, setTxnTypeFilter] = useState('');

  // Form states
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    contactNumber: '',
    managerName: '',
    active: true
  });



  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/warehouses');
      const data = res.data.data || res.data || [];
      setWarehouses(data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  };

  const fetchStock = async (whId) => {
    setStocksLoading(true);
    try {
      const res = await axios.get(`/warehouses/${whId}/stock`);
      const data = res.data.data || res.data || [];
      setStocks(data);
    } catch (e) {
      console.error(e);
      setStocks([]);
    } finally {
      setStocksLoading(false);
    }
  };

  const fetchTransactions = async (whId) => {
    setTransactionsLoading(true);
    try {
      const res = await axios.get(`/warehouses/${whId}/transactions`);
      const data = res.data.data || res.data || [];
      setTransactions(data);
    } catch (e) {
      console.error(e);
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleSelectWarehouse = (wh) => {
    setSelectedWarehouse(wh);
    fetchStock(wh.id);
  };

  const handleViewWarehouse = (wh, e) => {
    if (e) e.stopPropagation();
    setViewingWarehouse(wh);
    fetchStock(wh.id);
    fetchTransactions(wh.id);
    setActiveTab('stock');
    setStockSearch('');
    setTxnSearch('');
    setTxnTypeFilter('');
  };

  const handleOpenCreate = () => {
    setEditingWarehouse(null);
    setFormData({
      name: '',
      code: '',
      address: '',
      contactNumber: '',
      managerName: '',
      active: true
    });
    setShowFormModal(true);
  };

  const handleOpenEdit = (wh, e) => {
    e.stopPropagation();
    setEditingWarehouse(wh);
    setFormData({
      name: wh.name,
      code: wh.code,
      address: wh.address || '',
      contactNumber: wh.contactNumber || '',
      managerName: wh.managerName || '',
      active: wh.active
    });
    setShowFormModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.code.trim()) {
      toast.error('Name and Code are required');
      return;
    }

    try {
      if (editingWarehouse) {
        await axios.put(`/warehouses/${editingWarehouse.id}`, formData);
        toast.success('Warehouse updated successfully');
      } else {
        await axios.post('/warehouses', formData);
        toast.success('Warehouse created successfully');
      }
      setShowFormModal(false);
      fetchWarehouses();
      setSelectedWarehouse(null);
      setStocks([]);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to save warehouse');
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this warehouse?')) return;
    try {
      await axios.delete(`/warehouses/${id}`);
      toast.success('Warehouse deleted successfully');
      fetchWarehouses();
      setSelectedWarehouse(null);
      setStocks([]);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to delete warehouse');
    }
  };

  const filteredStocks = stocks.filter(item => {
    const term = stockSearch.toLowerCase();
    return (
      (item.itemName && item.itemName.toLowerCase().includes(term)) ||
      (item.itemCode && item.itemCode.toLowerCase().includes(term))
    );
  });

  const filteredTransactions = transactions.filter(txn => {
    const term = txnSearch.toLowerCase();
    const matchesSearch = 
      (txn.itemName && txn.itemName.toLowerCase().includes(term)) ||
      (txn.itemCode && txn.itemCode.toLowerCase().includes(term)) ||
      (txn.notes && txn.notes.toLowerCase().includes(term)) ||
      (txn.reason && txn.reason.toLowerCase().includes(term));
    
    const matchesType = !txnTypeFilter || txn.movementType === txnTypeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <div className="container" style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px' }}>
      {!viewingWarehouse && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 className="page-title" style={{ margin: 0 }}>Warehouse Management</h1>
          <button className="btn-primary" onClick={handleOpenCreate}>+ Add Warehouse</button>
        </div>
      )}



      {viewingWarehouse ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Header Card */}
          <div className="transaction-form-container" style={{ padding: '20px', borderRadius: '8px', background: '#fff', border: '1px solid #dee2e6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px' }}>
                    {viewingWarehouse.code}
                  </span>
                  <span className={`badge ${viewingWarehouse.active ? 'badge-success' : 'badge-danger'}`} style={{ backgroundColor: viewingWarehouse.active ? '#10b981' : '#ef4444', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                    {viewingWarehouse.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <h2 style={{ margin: '0 0 10px 0', fontSize: '24px', fontWeight: '700', color: '#111827' }}>{viewingWarehouse.name}</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '10px' }}>
                  <div>
                    <span style={{ fontSize: '11px', color: '#6b7280', display: 'block', textTransform: 'uppercase', fontWeight: '500' }}>Manager</span>
                    <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{viewingWarehouse.managerName || '—'}</span>
                  </div>
                  {viewingWarehouse.contactNumber && (
                    <div>
                      <span style={{ fontSize: '11px', color: '#6b7280', display: 'block', textTransform: 'uppercase', fontWeight: '500' }}>Contact</span>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{viewingWarehouse.contactNumber}</span>
                    </div>
                  )}
                  {viewingWarehouse.address && (
                    <div style={{ gridColumn: 'span 2' }}>
                      <span style={{ fontSize: '11px', color: '#6b7280', display: 'block', textTransform: 'uppercase', fontWeight: '500' }}>Address</span>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{viewingWarehouse.address}</span>
                    </div>
                  )}
                </div>
              </div>
              <button className="btn-secondary" style={{ padding: '8px 16px', fontWeight: '600' }} onClick={() => setViewingWarehouse(null)}>
                ← Back to List
              </button>
            </div>
          </div>

          {/* Tabs Container */}
          <div className="transaction-form-container" style={{ padding: '0', borderRadius: '8px', overflow: 'hidden', background: '#fff', border: '1px solid #dee2e6' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6', background: '#f9fafb' }}>
              <button 
                onClick={() => setActiveTab('stock')}
                style={{
                  padding: '15px 25px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === 'stock' ? '2px solid #3b82f6' : '2px solid transparent',
                  color: activeTab === 'stock' ? '#3b82f6' : '#4b5563',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Current Stock ({filteredStocks.length})
              </button>
              <button 
                onClick={() => setActiveTab('transactions')}
                style={{
                  padding: '15px 25px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === 'transactions' ? '2px solid #3b82f6' : '2px solid transparent',
                  color: activeTab === 'transactions' ? '#3b82f6' : '#4b5563',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Transaction History ({filteredTransactions.length})
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {activeTab === 'stock' ? (
                <div>
                  {/* Search Bar */}
                  <div style={{ marginBottom: '15px' }}>
                    <input 
                      type="text"
                      placeholder="Search items by name or code..."
                      value={stockSearch}
                      onChange={e => setStockSearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 15px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Stock Table */}
                  {stocksLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Loading stocks...</div>
                  ) : filteredStocks.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                      {stockSearch ? 'No items match your search.' : 'No stock items in this warehouse.'}
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr style={{ background: '#f9fafb' }}>
                            <th>Item Code</th>
                            <th>Item Name</th>
                            <th style={{ textAlign: 'right' }}>Current Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredStocks.map(st => (
                            <tr key={st.id}>
                              <td><strong>{st.itemCode}</strong></td>
                              <td>{st.itemName}</td>
                              <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#111827' }}>
                                {st.quantity} {st.unitOfMeasurement}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {/* Search & Filter Bar */}
                  <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <input 
                        type="text"
                        placeholder="Search transactions by item or notes..."
                        value={txnSearch}
                        onChange={e => setTxnSearch(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 15px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div style={{ width: '200px' }}>
                      <select
                        value={txnTypeFilter}
                        onChange={e => setTxnTypeFilter(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                          background: '#fff'
                        }}
                      >
                        <option value="">All Movement Types</option>
                        <option value="RECEIPT">Purchase Receipt</option>
                        <option value="RETURN">Purchase Return</option>
                        <option value="TRANSFER">Stock Transfer</option>
                        <option value="DAMAGE">Damage</option>
                        <option value="MAINTENANCE">Maintenance</option>
                      </select>
                    </div>
                  </div>

                  {/* Transactions Table */}
                  {transactionsLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Loading transactions...</div>
                  ) : filteredTransactions.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                      {txnSearch || txnTypeFilter ? 'No transactions match your search filters.' : 'No transactions recorded for this warehouse.'}
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table" style={{ fontSize: '13px' }}>
                        <thead>
                          <tr style={{ background: '#f9fafb' }}>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Item</th>
                            <th style={{ textAlign: 'right' }}>Qty</th>
                            <th>Source</th>
                            <th>Destination</th>
                            <th>Reason / Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTransactions.map(txn => {
                            const isPositive = parseFloat(txn.quantity) > 0;
                            return (
                              <tr key={txn.id}>
                                <td>{new Date(txn.movementDate).toLocaleDateString()} {new Date(txn.movementDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                <td>
                                  <span style={{
                                    padding: '3px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    backgroundColor: txn.movementType === 'RECEIPT' ? 'rgba(16, 185, 129, 0.1)' : 
                                                     txn.movementType === 'TRANSFER' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                    color: txn.movementType === 'RECEIPT' ? '#10b981' : 
                                           txn.movementType === 'TRANSFER' ? '#3b82f6' : '#ef4444'
                                  }}>
                                    {txn.movementType}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ fontWeight: '600' }}>{txn.itemName}</div>
                                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{txn.itemCode}</div>
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold', color: isPositive ? '#10b981' : '#ef4444' }}>
                                  {isPositive ? '+' : ''}{txn.quantity} {txn.unitOfMeasurement}
                                </td>
                                <td>{txn.sourceName}</td>
                                <td>{txn.destinationName}</td>
                                <td>
                                  <div style={{ fontStyle: 'italic', fontSize: '12px' }}>{txn.reason || '—'}</div>
                                  {txn.notes && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>Notes: {txn.notes}</div>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '25px', width: '100%', flexWrap: 'wrap' }}>
          
          {/* Warehouse List Table */}
          <div className="transaction-form-container" style={{ flex: 3, minWidth: '400px' }}>
            <h2 className="section-title">Warehouses</h2>
            {loading ? (
              <div>Loading...</div>
            ) : warehouses.length === 0 ? (
              <div>No warehouses found. Click Add Warehouse to create one.</div>
            ) : (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Manager</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {warehouses.map(wh => (
                      <tr 
                        key={wh.id} 
                        onClick={() => handleSelectWarehouse(wh)}
                        style={{ 
                          cursor: 'pointer',
                          backgroundColor: selectedWarehouse?.id === wh.id ? 'var(--menu-active-bg, #f0f7ff)' : 'transparent'
                        }}
                      >
                        <td><strong>{wh.code}</strong></td>
                        <td>{wh.name}</td>
                        <td>{wh.managerName || '—'}</td>
                        <td>
                          <span className={`badge ${wh.active ? 'badge-success' : 'badge-danger'}`} style={{ backgroundColor: wh.active ? '#10b981' : '#ef4444', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                            {wh.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button className="btn-secondary" style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px' }} onClick={(e) => handleViewWarehouse(wh, e)}>View</button>
                          <button className="btn-secondary" style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px' }} onClick={(e) => handleOpenEdit(wh, e)}>Edit</button>
                          <button className="btn-danger" style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }} onClick={(e) => handleDelete(wh.id, e)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Selected Warehouse Stock Details */}
          <div className="transaction-form-container" style={{ flex: 2, minWidth: '300px' }}>
            <h2 className="section-title">Warehouse Stock Levels</h2>
            {!selectedWarehouse ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                Select a warehouse from the table to view its current stock levels.
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                  <h3 style={{ margin: '0 0 5px 0' }}>{selectedWarehouse.name}</h3>
                  <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Manager: {selectedWarehouse.managerName || '—'}</p>
                  <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: '#666' }}>Address: {selectedWarehouse.address || '—'}</p>
                </div>

                {stocksLoading ? (
                  <div>Loading stock...</div>
                ) : stocks.length === 0 ? (
                  <div style={{ padding: '10px 0', fontStyle: 'italic', color: '#888' }}>No stock items in this warehouse.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table" style={{ fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          <th style={{ padding: '8px' }}>Item Code</th>
                          <th style={{ padding: '8px' }}>Item Name</th>
                          <th style={{ padding: '8px', textAlign: 'right' }}>Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stocks.map(st => (
                          <tr key={st.id}>
                            <td style={{ padding: '8px' }}>{st.itemCode}</td>
                            <td style={{ padding: '8px' }}>{st.itemName}</td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{st.quantity} {st.unitOfMeasurement}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      )}

      {/* Create / Edit Warehouse Modal */}
      {showFormModal && (
        <div className="modal-overlay" style={{ display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', zIndex: 1000, overflowY: 'auto', padding: '20px' }} onClick={() => setShowFormModal(false)}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '500px', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>{editingWarehouse ? 'Edit Warehouse' : 'New Warehouse'}</h2>
              <button className="close-button" style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }} onClick={() => setShowFormModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div className="modal-body" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px', overflowY: 'auto' }}>
                <div className="form-group">
                  <label>Warehouse Code *</label>
                  <input 
                    type="text" 
                    value={formData.code} 
                    onChange={e => setFormData(prev => ({ ...prev, code: e.target.value }))}
                    placeholder="e.g. WH-A"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Warehouse Name *</label>
                  <input 
                    type="text" 
                    value={formData.name} 
                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Main Warehouse"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Manager Name</label>
                  <input 
                    type="text" 
                    value={formData.managerName} 
                    onChange={e => setFormData(prev => ({ ...prev, managerName: e.target.value }))}
                    placeholder="e.g. John Doe"
                  />
                </div>
                <div className="form-group">
                  <label>Contact Number</label>
                  <input 
                    type="text" 
                    value={formData.contactNumber} 
                    onChange={e => setFormData(prev => ({ ...prev, contactNumber: e.target.value }))}
                    placeholder="e.g. +1 234 567 890"
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <textarea 
                    value={formData.address} 
                    onChange={e => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Warehouse location details..."
                    rows="3"
                  />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input 
                    type="checkbox" 
                    id="wh-active"
                    checked={formData.active} 
                    onChange={e => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                  />
                  <label htmlFor="wh-active" style={{ marginBottom: 0 }}>Active</label>
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowFormModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Warehouse</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
